import { copy, escapeHtml, formatCopy, state } from "./runtime.js";
import { SYMPTOMS } from "../../app.js";
import { symptomLabel, symptomListLabel } from "../../symptom-labels.js";
import { MAP_LAB_LAYERS } from "./constants.js";
import { mapLabActiveSymptoms, mapLabVisibility, recomputeMapLabDerived } from "./data.js";

// Symptom filter options: the app's symptom vocabulary minus the catch-all "Other".
// Computed lazily (not at module top level) — app.js and this module import each other,
// so SYMPTOMS isn't initialized yet while this module is first evaluated.
function symptomFilterOptions() {
  return SYMPTOMS.filter((symptom) => symptom !== "Other");
}
import { applyMapLabDotsVisibility, applyMapLabPinVisibility } from "./layers.js";

export function reportDotLegendMarkup() {
  return dotLegendMarkup();
}

// Outbreak-dots legend: red = symptom reports, dot size = that ZIP's rank against the
// other ZIPs drawn. Mirrors the baked dot sprite on the map.

export function dotLegendMarkup() {
  // Symptom filter: every surviving dot carries all the selected symptoms, so the legend
  // names them.
  const symptoms = mapLabActiveSymptoms();
  if (symptoms.length) {
    const symptomText = symptomListLabel(symptoms, state.lang);
    return `
    <div class="report-dot-legend" aria-label="${escapeHtml(copy("map_legend"))}">
      <span><span class="report-dot sick" aria-hidden="true"></span>${escapeHtml(copy("map_sick_reports"))} &middot; ${escapeHtml(symptomText)}</span>
      <span><span class="legend-size" aria-hidden="true"></span>${escapeHtml(formatCopy("map_dot_size_symptom", { symptom: symptomText }))}</span>
    </div>
  `;
  }
  return `
    <div class="report-dot-legend" aria-label="${escapeHtml(copy("map_legend"))}">
      <span><span class="report-dot sick" aria-hidden="true"></span>${escapeHtml(copy("map_sick_reports"))}</span>
      <span><span class="legend-size" aria-hidden="true"></span>${escapeHtml(copy("map_dot_size_total"))}</span>
    </div>
  `;
}

// Re-render legends in place after the symptom filter changes their composition.

export function renderMapLabDotLegend() {
  document.querySelectorAll(".report-dot-legend").forEach((node) => {
    node.outerHTML = reportDotLegendMarkup();
  });
}


// PREVIEW helper: category show/hide toggle row. Every category shows a solid color swatch
// matching its dots on the map — people the sick red, animal/environment their point
// color. Markers still render as emoji.

export function mapLabLayerToggleMarkup() {
  const visible = mapLabVisibility();
  // Each category is an on/off sliding switch, with a color swatch + label on the left and
  // the switch pushed to the right of the row.
  return `
    <div class="map-lab-layer-switches" role="group" aria-label="${escapeHtml(copy("map_categories_shown"))}">
      ${MAP_LAB_LAYERS.map(
        (layer) => {
          const label = copy(layer.labelKey);
          return (
          `<div class="map-lab-toggle map-lab-layer-row">` +
          `<span class="map-lab-toggle-label map-lab-layer-name"><span class="map-lab-swatch" style="background:${layer.swatch}"></span>${escapeHtml(label)}</span>` +
          `<button type="button" role="switch" data-action="map-lab-layer" data-category="${layer.category}" aria-checked="${visible[layer.category]}" aria-label="${escapeHtml(formatCopy("map_show_category", { category: label }))}" class="map-lab-switch${visible[layer.category] ? " active" : ""}"><span class="map-lab-switch-knob" aria-hidden="true"></span></button>` +
          `</div>`
          );
        }
      ).join("")}
    </div>
  `;
}

export function setMapLabCategoryVisible(category) {
  const visible = mapLabVisibility();
  if (!Object.hasOwn(visible, category)) return;
  visible[category] = !visible[category];
  if (category === "people") {
    applyMapLabDotsVisibility();
  } else {
    applyMapLabPinVisibility(category);
  }
  document
    .querySelectorAll(`[data-action='map-lab-layer'][data-category='${category}']`)
    .forEach((button) => {
      button.classList.toggle("active", visible[category]);
      button.setAttribute("aria-checked", String(visible[category]));
    });
}

// Symptom filter: a dropdown button opening a checkbox menu, narrowing people reports to sick
// ones carrying EVERY checked symptom. Multiple checks AND together. "All symptoms" is the
// first checkbox in that same list rather than a clear button, so the unfiltered state reads as
// a checked choice instead of a menu with nothing selected; checking it clears the rest, and it
// cannot be unchecked on its own (nothing checked IS "all symptoms").
// Applies in both dots and shading mode — under a filter the dots show only where those
// symptoms were reported together, sized by the matching report count.

// Dropdown button text: the cleared label, or the one symptom. Past that the button stays
// neutral — naming the selections is the note's job, and they would not fit the rail width.

export function symptomFilterButtonLabel(active = mapLabActiveSymptoms()) {
  if (active.length === 0) return copy("map_all_symptoms");
  if (active.length === 1) return symptomLabel(active[0], state.lang);
  return copy("map_symptoms_selected");
}

// What sits under the dropdown once more than one symptom is checked, as two stacked pieces
// rather than a sentence to read: the selections as tags (up to three, then a "+N more" tag),
// and a footnote caption spelling out that they AND together. Returns markup, so callers must
// render it as HTML, not text.

const SYMPTOM_TAG_LIMIT = 3;

export function symptomSelectionSummaryMarkup(active = mapLabActiveSymptoms()) {
  if (active.length < 2) return "";
  const tags = active
    .slice(0, SYMPTOM_TAG_LIMIT)
    .map((value) => `<li class="map-lab-symptom-tag">${escapeHtml(symptomLabel(value, state.lang))}</li>`);
  if (active.length > SYMPTOM_TAG_LIMIT) {
    const overflow = formatCopy("map_symptom_more_tag", { count: active.length - SYMPTOM_TAG_LIMIT });
    tags.push(`<li class="map-lab-symptom-tag map-lab-symptom-tag-more">${escapeHtml(overflow)}</li>`);
  }
  return (
    `<ul class="map-lab-symptom-tags">${tags.join("")}</ul>` +
    `<p class="map-lab-symptom-caption">` +
    `<span class="map-lab-symptom-caption-mark" aria-hidden="true">*</span>` +
    `${escapeHtml(copy("map_symptom_and_hint"))}</p>`
  );
}

export function mapLabSymptomFilterMarkup() {
  const active = mapLabActiveSymptoms();
  const options = symptomFilterOptions()
    .map((value) => {
      const checked = active.includes(value) ? " checked" : "";
      return (
        `<label class="map-lab-symptom-option">` +
        `<input type="checkbox" data-action="map-lab-symptom" value="${escapeHtml(value)}"${checked} />` +
        `<span>${escapeHtml(symptomLabel(value, state.lang))}</span>` +
        `</label>`
      );
    })
    .join("");
  return `
    <div class="map-lab-toggle map-lab-symptom" role="group" aria-label="${escapeHtml(copy("map_symptom_filter"))}">
      <span class="map-lab-toggle-label">${escapeHtml(copy("map_symptom"))}:</span>
      <div class="map-lab-symptom-menu">
        <button type="button" class="map-lab-symptom-select" data-action="map-lab-symptom-menu" aria-haspopup="true" aria-expanded="false">
          <span class="map-lab-symptom-select-text" data-map-lab-symptom-label>${escapeHtml(symptomFilterButtonLabel(active))}</span>
          <span class="map-lab-symptom-caret" aria-hidden="true">▾</span>
        </button>
        <div class="map-lab-symptom-panel" role="group" aria-label="${escapeHtml(copy("map_filter_symptom"))}" data-map-lab-symptom-panel hidden>
          <label class="map-lab-symptom-option map-lab-symptom-option-all">
            <input type="checkbox" data-action="map-lab-symptom-all"${active.length === 0 ? " checked" : ""} />
            <span>${escapeHtml(copy("map_all_symptoms"))}</span>
          </label>
          ${options}
        </div>
      </div>
      <div class="map-lab-symptom-summary" data-map-lab-symptom-summary${active.length > 1 ? "" : " hidden"}>${symptomSelectionSummaryMarkup(active)}</div>
    </div>
  `;
}

// Open/close the checkbox menu. The menu stays open while symptoms are checked so several can
// be combined in one pass; an outside click, Escape, or a re-render closes it.

export function setMapLabSymptomMenuOpen(open) {
  document.querySelectorAll("[data-map-lab-symptom-panel]").forEach((panel) => {
    panel.hidden = !open;
  });
  document.querySelectorAll("[data-action='map-lab-symptom-menu']").forEach((button) => {
    button.setAttribute("aria-expanded", String(open));
  });
}

export function toggleMapLabSymptomMenu() {
  const panel = document.querySelector("[data-map-lab-symptom-panel]");
  setMapLabSymptomMenuOpen(Boolean(panel?.hidden));
}

// Check/uncheck one symptom, keeping the app's canonical SYMPTOMS order so the button label and
// the legend read the same way regardless of the order boxes were ticked.

export function toggleMapLabSymptom(symptom, checked) {
  const active = new Set(mapLabActiveSymptoms());
  if (checked) {
    active.add(symptom);
  } else {
    active.delete(symptom);
  }
  setMapLabSymptoms(symptomFilterOptions().filter((value) => active.has(value)));
}

// Set the active symptom filter and re-summarize (recompute drives the map + count note).

export function setMapLabSymptoms(symptoms) {
  state.mapLabSymptoms = Array.isArray(symptoms)
    ? symptoms.filter((symptom) => typeof symptom === "string" && symptom)
    : [];
  const active = mapLabActiveSymptoms();
  document.querySelectorAll("[data-action='map-lab-symptom']").forEach((checkbox) => {
    const next = active.includes(checkbox.value);
    if (checkbox.checked !== next) checkbox.checked = next;
  });
  // "All symptoms" mirrors the cleared state, which also re-checks it if it was the box the
  // user just unchecked — unchecking it would ask for no filter and no "all", which is nothing.
  document.querySelectorAll("[data-action='map-lab-symptom-all']").forEach((checkbox) => {
    const next = active.length === 0;
    if (checkbox.checked !== next) checkbox.checked = next;
  });
  document.querySelectorAll("[data-map-lab-symptom-label]").forEach((node) => {
    node.textContent = symptomFilterButtonLabel(active);
  });
  document.querySelectorAll("[data-map-lab-symptom-summary]").forEach((node) => {
    node.innerHTML = symptomSelectionSummaryMarkup(active);
    node.hidden = active.length < 2;
  });
  renderMapLabDotLegend();
  recomputeMapLabDerived();
}
